import { useState, useMemo, useEffect, useRef, useDeferredValue, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams, useBlocker } from "react-router-dom";
import { Minus, Plus, Star, Copy, Download, ImageDown, ChevronDown, Ellipsis, NotebookPen, X } from "lucide-react";
import { getCardById, getCardImage, toThumbUrl, filterCards, getAllRaces, ABILITY_KEYWORD_GROUPS, DECK_FORMATS, FORMAT_LABELS, DEFAULT_FORMAT } from "@dm-online/shared";
import type { Card, CardType, DeckFormat, CivFilterToken, CivMatchMode } from "@dm-online/shared";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CIV_LABELS, CIV_ORDER, civPillStyle, compareDeckCards } from "@/lib/civ";
import { renderDeckImage } from "@/lib/deckImage";
import { formatDeckCode, normalizeDeckCode } from "@/lib/deckCode";
import AppHeader from "@/components/ui/AppHeader";
import DeckAnalysis from "@/components/game/DeckAnalysis";
import DeckImageImport from "@/components/game/DeckImageImport";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_LABELS: Record<CardType, string> = {
  creature: "クリーチャー", evolution_creature: "進化",
  evolution_creature_mana: "進化(マナ)", evolution_creature_v: "進化V",
  evolution_creature_gv: "進化GV", spell: "呪文",
  cross_gear: "クロスギア", evolution_cross_gear: "進化クロスギア", castle: "城",
  psychic_creature: "サイキック", evolution_psychic_creature: "進化サイキック",
  psychic_super_creature: "サイキックS", dragheart_weapon: "ドラグハート(武器)",
  dragheart_fortress: "ドラグハート(城)", dragheart_creature: "ドラグハート(龍)",
  exile_creature: "エグザイル", evolution_exile_creature: "進化エグザイル",
};

// カード検索結果を一度に描画する枚数（以降はスクロールで追加）
const SEARCH_PAGE_SIZE = 60;
const MAX_DECK_SIZE = 40;
const MAX_COPIES = 4;
// メモの上限文字数（worker 側の上限と揃える）
const MEMO_MAX_LENGTH = 2000;
// 超次元ゾーン（サイキック/ドラグハート）の目安上限。手動運用のため保存はブロックしない
const HYPERSPATIAL_SOFT_CAP = 8;

// Select は空文字値を許可しないため「指定なし」を表すセンチネル
const ALL = "__all__";

// デッキ内容はPCでは1行8枚固定（カードは幅いっぱいに詰める）。スマホはカードが小さくなりすぎないよう5列。
const CARD_GRID_DECK = "grid grid-cols-5 gap-1.5 sm:grid-cols-8";
// カード検索はPCでは幅固定パネルなので、カード幅を固定して自動折り返しする。スマホ（ボトムシート）は3列。
const CARD_GRID_FIXED = "grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:[grid-template-columns:repeat(auto-fill,112px)]";

// 文明チップ（無色を先頭に）と多色/単色の一致モードの表示定義
const CIV_TOKENS: CivFilterToken[] = ["colorless", ...CIV_ORDER];
const CIV_TOKEN_LABELS: Record<CivFilterToken, string> = { colorless: "無色", ...CIV_LABELS };
const CIV_MODES: { value: CivMatchMode; label: string }[] = [
  { value: "single", label: "単色" },
  { value: "multi", label: "多色" },
  { value: "exact", label: "完全一致" },
];

// 文明・モードの絞り込みチップ。選択時は activeStyle で塗り、非選択時は枠のみ。
function FilterChip({ active, activeStyle, onClick, children }: {
  active: boolean;
  activeStyle?: CSSProperties;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={active ? activeStyle : undefined}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-transparent"
          : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

// チップ選択時の塗り色。文明は文明色（無色はニュートラルなグレー）、モードはプライマリ。
function civActiveStyle(token: CivFilterToken): CSSProperties {
  if (token === "colorless") {
    return { color: "var(--foreground)", backgroundColor: "var(--muted)", borderColor: "var(--border)" };
  }
  return civPillStyle(token);
}
const MODE_ACTIVE_STYLE: CSSProperties = {
  color: "var(--primary-foreground)",
  backgroundColor: "var(--primary)",
};

// サイキック/ドラグハートは山札に入らず超次元ゾーンに置くカード
function isHyperspatial(card?: Card | null): boolean {
  return card?.category === "psychic" || card?.category === "dragheart";
}

interface DeckData {
  id: string;
  name: string;
  card_ids: string[];
  key_card_id?: string | null;
  format?: DeckFormat;
  // 共有用デッキコード。保存済みデッキにのみ存在する
  share_code?: string | null;
  // 自由記述メモ（本人用。デッキコードでは共有されない）
  memo?: string;
}

// デッキコードで取得した他ユーザーのデッキ（インポート元）
interface SharedDeckData {
  name: string;
  cardIds: string[];
  key_card_id?: string | null;
  format?: DeckFormat;
  share_code?: string | null;
}

// カードサムネイル。width 指定時は固定サイズ、省略時はセル全幅（アスペクト比63:88）で表示。
// 画像が無い場合も同サイズの枠を描画してレイアウトを崩さない。
// 通常はサムネイル画像を使い、大きく見せる箇所だけ full で原寸画像にする
function CardThumb({ card, width, imageUrl, full }: { card: Card; width?: number; imageUrl?: string; full?: boolean }) {
  const style = width ? { width, height: Math.round(width * 1.4) } : undefined;
  const sizeClass = width ? "shrink-0" : "w-full aspect-63/88";
  // imageUrl 指定時は選択中イラストを優先。未指定なら既定（正規カードの画像）
  const src = full ? (imageUrl ?? card.imageUrl) : toThumbUrl(imageUrl ?? card.imageUrl);
  if (!src) {
    return <div style={style} className={`rounded-md bg-muted ${sizeClass}`} />;
  }
  return (
    <img
      src={src}
      alt={card.name}
      loading="lazy"
      style={style}
      className={`rounded-md object-cover ${sizeClass}`}
    />
  );
}

// 未保存の変更を判定するためのスナップショット。カードの並び順は本質でないのでソートして比較する
function deckSnapshot(
  name: string,
  format: DeckFormat,
  keyCardId: string | null,
  deckIds: string[],
  memo: string
): string {
  return JSON.stringify({ name, format, keyCardId, ids: [...deckIds].sort(), memo });
}

// スナップショットのキーカードだけ差し替える（キーカード単体保存の基準更新に使う）
function withKeyCard(snapshot: string, keyCardId: string | null): string {
  const s = JSON.parse(snapshot) as { name: string; format: DeckFormat; ids: string[]; memo: string };
  return deckSnapshot(s.name, s.format, keyCardId, s.ids, s.memo);
}

const INITIAL_DECK_NAME = "新しいデッキ";

// このページ内のナビゲーション（インポート後の /deck 戻し、保存後の URL 同期）は離脱ではないので通す
function isDeckBuilderPath(pathname: string): boolean {
  return /^\/deck(\/|$)/.test(pathname);
}

export default function DeckBuilderPage() {
  const navigate = useNavigate();
  const { deckId: urlDeckId } = useParams<{ deckId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeDeckId, setActiveDeckId] = useState<string | null>(urlDeckId ?? null);
  const [deckName, setDeckName] = useState(INITIAL_DECK_NAME);
  const [deckIds, setDeckIds] = useState<string[]>([]);
  // 明示的に選んだイラスト（正規カードID → 刷りID）。デッキに0枚でも選択を保持できるよう
  // deckIds とは別に持つ。追加時・サムネイル・モーダルの選択状態で優先的に参照する。
  const [artChoice, setArtChoice] = useState<Map<string, string>>(new Map());
  const [format, setFormat] = useState<DeckFormat>(DEFAULT_FORMAT);
  const [keyCardId, setKeyCardId] = useState<string | null>(null);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [memo, setMemo] = useState("");
  const [memoOpen, setMemoOpen] = useState(false);
  const [qtyCardId, setQtyCardId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!urlDeckId);

  // キーカード単体保存の世代。古いリクエストの結果で基準を上書きしないために使う
  const keySaveSeq = useRef(0);

  // 最後に保存（または読み込み）した時点の内容。これと現在の内容が違えば「未保存の変更あり」
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    deckSnapshot(INITIAL_DECK_NAME, DEFAULT_FORMAT, null, [], "")
  );

  // デッキコード（共有用ハッシュ）。保存済みデッキを開いているときだけ表示する
  const [shareCode, setShareCode] = useState<string | null>(null);
  // デッキコード・画像からのインポート
  const [imageExporting, setImageExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(searchParams.get("import") !== null);
  const [importMode, setImportMode] = useState<"code" | "image">("code");
  const [importCode, setImportCode] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importedFrom, setImportedFrom] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [selectedCivs, setSelectedCivs] = useState<CivFilterToken[]>([]);
  const [civMode, setCivMode] = useState<CivMatchMode | null>(null);
  const [typeFilter, setTypeFilter] = useState<CardType | "">("");
  const [costFilter, setCostFilter] = useState<number | "">("");
  const [raceFilter, setRaceFilter] = useState<string>("");
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [abilityOpen, setAbilityOpen] = useState(false);
  // スマホではカード検索を下からせり上がるシートで出す（PC=lg以上では常時表示の右パネル）
  const [searchOpen, setSearchOpen] = useState(false);

  const toggleCiv = (t: CivFilterToken) =>
    setSelectedCivs((prev) => (prev.includes(t) ? prev.filter((c) => c !== t) : [...prev, t]));
  const toggleCivMode = (m: CivMatchMode) =>
    setCivMode((prev) => (prev === m ? null : m));
  const toggleKeyword = (k: string) =>
    setSelectedKeywords((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  useEffect(() => {
    if (!urlDeckId) return;
    api.get<DeckData>(`/decks/${urlDeckId}`).then((res) => {
      if (res.ok && res.data) {
        setActiveDeckId(res.data.id);
        setDeckName(res.data.name);
        setDeckIds(res.data.card_ids);
        setArtChoice(new Map()); // 読込時は保存済みの刷りIDから導出し直す
        setFormat(res.data.format ?? DEFAULT_FORMAT);
        setKeyCardId(res.data.key_card_id ?? null);
        setShareCode(res.data.share_code ?? null);
        setMemo(res.data.memo ?? "");
        setSavedSnapshot(
          deckSnapshot(
            res.data.name,
            res.data.format ?? DEFAULT_FORMAT,
            res.data.key_card_id ?? null,
            res.data.card_ids,
            res.data.memo ?? ""
          )
        );
      }
      setLoading(false);
    });
  }, [urlDeckId]);

  // キーカードがデッキから無くなったら自動で解除する
  // keyCardId は正規ID、deckIds は刷りID なので正規IDへ解決してから照合する
  useEffect(() => {
    if (keyCardId && !deckIds.some((id) => (getCardById(id)?.id ?? id) === keyCardId)) setKeyCardId(null);
  }, [deckIds, keyCardId]);

  // 絞り込みは入力より低優先で計算する（入力欄・チップの反応を先に返し、結果は少し遅れて更新）
  const filterArgs = useMemo(() => ({
    civs: selectedCivs.length > 0 ? selectedCivs : undefined,
    civMode: civMode ?? undefined,
    type: typeFilter || undefined,
    query: query || undefined,
    cost: costFilter !== "" ? costFilter : undefined,
    race: raceFilter || undefined,
    keywords: selectedKeywords.length > 0 ? selectedKeywords : undefined,
    format,
  }), [selectedCivs, civMode, typeFilter, query, costFilter, raceFilter, selectedKeywords, format]);
  const deferredFilterArgs = useDeferredValue(filterArgs);
  const filteredCards = useMemo(() => filterCards(deferredFilterArgs), [deferredFilterArgs]);

  // 検索結果は全件を一度に描画せず、スクロールに合わせて段階的に増やす（DOM・画像読み込みを抑える）
  const [visibleCount, setVisibleCount] = useState(SEARCH_PAGE_SIZE);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setVisibleCount(SEARCH_PAGE_SIZE); }, [filteredCards]);
  const visibleCards = useMemo(() => filteredCards.slice(0, visibleCount), [filteredCards, visibleCount]);
  const hasMore = visibleCount < filteredCards.length;
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisibleCount((n) => n + SEARCH_PAGE_SIZE);
      },
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visibleCount, searchOpen]);

  // 枚数は正規カードID（再録違いをまとめた id）で数える。
  // deckIds には選んだイラストの刷りID が入るため、そのまま数えると別イラストが別カード扱いになってしまう。
  const deckCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const id of deckIds) {
      const canon = getCardById(id)?.id ?? id;
      map.set(canon, (map.get(canon) ?? 0) + 1);
    }
    return map;
  }, [deckIds]);

  // 正規カードID → このデッキで選んでいるイラストの刷りID。
  // まずデッキ内の刷りIDから導出し、明示選択(artChoice)があればそれで上書きする
  // （明示選択は0枚でも保持されるので、追加前でもイラストを選べる）。
  const chosenArt = useMemo(() => {
    const map = new Map<string, string>();
    for (const id of deckIds) {
      const canon = getCardById(id)?.id;
      if (canon) map.set(canon, id);
    }
    for (const [canon, printId] of artChoice) map.set(canon, printId);
    return map;
  }, [deckIds, artChoice]);

  const deckCardsSorted = useMemo(() => {
    return [...new Set(deckIds)]
      .map((id) => getCardById(id)!)
      .filter(Boolean)
      .sort(compareDeckCards);
  }, [deckIds]);

  // デッキ内容表示用: コピーを含む全カードをコスト→文明順に並べる（同一カードはまとめて表示）
  const deckCardsAll = useMemo(() => {
    return deckIds
      .map((id) => getCardById(id))
      .filter((c): c is Card => !!c)
      .sort(compareDeckCards);
  }, [deckIds]);

  // メイン（山札）と超次元（サイキック/ドラグハート）を分けて表示・集計する
  const deckMainCards = useMemo(() => deckCardsAll.filter((c) => !isHyperspatial(c)), [deckCardsAll]);
  const deckHyperCards = useMemo(() => deckCardsAll.filter((c) => isHyperspatial(c)), [deckCardsAll]);
  const mainCount = deckMainCards.length;
  const hyperCount = deckHyperCards.length;

  const keyCard = keyCardId ? deckCardsSorted.find((c) => c.id === keyCardId) : undefined;
  const qtyCard = qtyCardId ? getCardById(qtyCardId) : undefined;
  const qtyCount = qtyCard ? (deckCounts.get(qtyCard.id) ?? 0) : 0;
  // 数量モーダルで選択中の刷りID（＝この正規カードで選んでいるイラスト）
  const qtyArtId = qtyCard ? (chosenArt.get(qtyCard.id) ?? qtyCard.id) : undefined;

  async function exportDeckImage() {
    setImageExporting(true);
    const toastId = toast.loading("デッキ画像を生成中...");
    try {
      const printId = (c: Card) => chosenArt.get(c.id) ?? c.id;
      const blob = await renderDeckImage({
        name: deckName,
        mainIds: deckMainCards.map(printId),
        hyperIds: deckHyperCards.map(printId),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${deckName.trim() || "deck"}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("デッキ画像を保存しました", { id: toastId });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "画像の生成に失敗しました", { id: toastId });
    } finally {
      setImageExporting(false);
    }
  }

  function addCard(card: Card) {
    // 40枚を超えて追加できる（保存時に40枚ちょうどを要求）。同名は最大MAX_COPIESまで
    if ((deckCounts.get(card.id) ?? 0) >= MAX_COPIES) return;
    // すでにこのカードのイラストを選んでいればそれを踏襲、無ければ既定（正規ID）
    const printId = chosenArt.get(card.id) ?? card.id;
    setDeckIds((prev) => [...prev, printId]);
    setSaveStatus("idle");
  }

  function removeCard(cardId: string) {
    // cardId は正規ID。deckIds には刷りID が入るので、正規IDが一致する最後の1枚を消す
    const canon = getCardById(cardId)?.id ?? cardId;
    let idx = -1;
    for (let i = deckIds.length - 1; i >= 0; i--) {
      if ((getCardById(deckIds[i])?.id ?? deckIds[i]) === canon) { idx = i; break; }
    }
    if (idx === -1) return;
    setDeckIds((prev) => prev.filter((_, i) => i !== idx));
    setSaveStatus("idle");
  }

  // カードのイラスト（刷り）を選ぶ。0枚でも選択を保持し、デッキ内に同カードがあれば
  // 全コピーを選んだ刷りID に置き換える。
  function chooseArt(canonId: string, printId: string) {
    setArtChoice((prev) => new Map(prev).set(canonId, printId));
    setDeckIds((prev) => prev.map((id) => ((getCardById(id)?.id ?? id) === canonId ? printId : id)));
    setSaveStatus("idle");
  }

  // キーカード（主軸）は1枚のみ。null で解除。
  // 保存済みデッキで他に未保存の変更が無いときは、キーカードだけを即座に保存する
  // （デッキ内容が保存済みの状態と一致していないと、サーバー側で山札に無いカードが主軸になり得るため）
  async function chooseKeyCard(cardId: string | null) {
    setKeyCardId(cardId);
    setKeyModalOpen(false);
    setSaveStatus("idle");

    const onlyKeyDiffers =
      deckSnapshot(deckName, format, null, deckIds, memo) === withKeyCard(savedSnapshot, null);
    if (!activeDeckId || !onlyKeyDiffers) return;

    const seq = ++keySaveSeq.current;
    setSaveStatus("saving");
    setSaveError(null);
    const res = await api.put(`/decks/${activeDeckId}`, { keyCardId: cardId });
    // 続けて別のキーカードが選ばれていたら、こちらの結果は捨てる
    if (seq !== keySaveSeq.current) return;
    if (res.ok) {
      setSavedSnapshot((prev) => withKeyCard(prev, cardId));
      setSaveStatus("saved");
      toast.success("キーカードを保存しました");
    } else {
      // 失敗したら未保存のまま。通常の「保存」でまとめて保存できる
      const message = res.error ?? "キーカードの保存に失敗しました";
      setSaveError(message);
      setSaveStatus("error");
      toast.error(message);
    }
  }

  // 自分のデッキのデッキコードをコピーする（他のアカウントに渡すとインポートしてもらえる）
  async function copyShareCode() {
    if (!shareCode) return;
    const code = formatDeckCode(shareCode);
    try {
      await navigator.clipboard.writeText(code);
      toast.success("デッキコードをコピーしました", { description: code });
    } catch {
      // クリップボードが使えない環境ではトーストに出したコードを手動でコピーしてもらう
      toast.error("コピーできませんでした", { description: code });
    }
  }

  // インポートダイアログの開閉。?import= 付きで開かれた場合はクエリも落とす
  function toggleImportDialog(open: boolean) {
    setImportOpen(open);
    if (open) return;
    setImportError(null);
    if (searchParams.has("import")) {
      const next = new URLSearchParams(searchParams);
      next.delete("import");
      setSearchParams(next, { replace: true });
    }
  }

  // デッキコードで他アカウントのデッキを取り込む。
  // 取り込んだ内容は「未保存の新規デッキ」として扱い、保存した時点で自分のデッキとしてコピーされる
  async function importByCode() {
    const code = normalizeDeckCode(importCode);
    if (!code) {
      setImportError("デッキコードを入力してください");
      return;
    }
    setImporting(true);
    setImportError(null);
    const res = await api.get<SharedDeckData>(`/decks/code/${code}`);
    setImporting(false);
    if (!res.ok || !res.data) {
      setImportError(res.error ?? "デッキを取得できませんでした");
      return;
    }

    const imported = res.data;
    applyImportedDeck({
      name: imported.name,
      cardIds: imported.cardIds,
      format: imported.format ?? DEFAULT_FORMAT,
      keyCardId: imported.key_card_id ?? null,
      from: formatDeckCode(imported.share_code ?? code),
    });
    setImportCode("");
  }

  // 画像から読み取ったカードを取り込む。フォーマットは編集中のものを引き継ぐ
  function importFromImage(cardIds: string[]) {
    applyImportedDeck({ name: INITIAL_DECK_NAME, cardIds, format, keyCardId: null, from: "画像から読み取ったデッキ" });
  }

  // インポートした内容を「未保存の新規デッキ」として読み込む（デッキコード・画像で共通）
  function applyImportedDeck(imported: {
    name: string;
    cardIds: string[];
    format: DeckFormat;
    keyCardId: string | null;
    from: string;
  }) {
    setDeckName(imported.name);
    setDeckIds(imported.cardIds);
    setArtChoice(new Map()); // インポート内容の刷りIDから導出し直す
    setFormat(imported.format);
    setKeyCardId(imported.keyCardId);
    setMemo(""); // メモは共有されないので新規デッキとして空から始める
    // 元デッキ（他人のデッキ）や編集中だったデッキを上書きしないよう、新規デッキに切り替える
    setActiveDeckId(null);
    setShareCode(null);
    setSaveStatus("idle");
    setSaveError(null);
    setImportedFrom(imported.from);
    toggleImportDialog(false);
    // /deck/:deckId を編集中だった場合は新規デッキの URL に戻す（同一ルートなので状態は保たれる）
    if (urlDeckId) navigate("/deck");
  }

  // skipUrlSync: 離脱ダイアログからの保存では直後に別ページへ移動するため URL 同期は行わない
  async function saveDeck({ skipUrlSync = false }: { skipUrlSync?: boolean } = {}): Promise<boolean> {
    // 保存はメインデッキ（超次元を除く）がちょうど40枚のときのみ。超次元カードは枚数外。
    if (mainCount !== MAX_DECK_SIZE) {
      const message = `保存にはメイン${MAX_DECK_SIZE}枚必要です（現在${mainCount}枚）`;
      setSaveError(message);
      setSaveStatus("error");
      toast.error(message);
      return false;
    }
    setSaveStatus("saving");
    setSaveError(null);
    // キーカードは在デッキの1枚のみ有効（deckIds は刷りID なので正規IDへ解決して照合）
    const keyToSave =
      keyCardId && deckIds.some((id) => (getCardById(id)?.id ?? id) === keyCardId) ? keyCardId : null;
    try {
      let res;
      if (activeDeckId) {
        res = await api.put(`/decks/${activeDeckId}`, { name: deckName, cardIds: deckIds, keyCardId: keyToSave, format, memo });
      } else {
        res = await api.post<DeckData>("/decks", { name: deckName, cardIds: deckIds, keyCardId: keyToSave, format, memo });
        if (res.ok && res.data) {
          setActiveDeckId(res.data.id);
          setShareCode(res.data.share_code ?? null);
          if (!skipUrlSync) navigate(`/deck/${res.data.id}`, { replace: true });
        }
      }
      if (!res.ok) setSaveError(res.error ?? null);
      setSaveStatus(res.ok ? "saved" : "error");
      if (res.ok) toast.success("デッキを保存しました");
      else toast.error(res.error ?? "保存に失敗しました");
      // 保存できた内容を「変更なし」の基準にする
      if (res.ok) setSavedSnapshot(deckSnapshot(deckName, format, keyToSave, deckIds, memo));
      return res.ok;
    } catch {
      // 想定外の例外でも「保存中」のまま固まらないようにする
      setSaveError("保存に失敗しました");
      setSaveStatus("error");
      toast.error("保存に失敗しました");
      return false;
    }
  }

  // ---- 未保存の変更の離脱ガード ----
  const isDirty = !loading && deckSnapshot(deckName, format, keyCardId, deckIds, memo) !== savedSnapshot;
  const [leaveSaving, setLeaveSaving] = useState(false);

  // 画面内（React Router）の移動をブロックして確認ダイアログを出す。
  // 戻る/進むも同じ経路を通るのでまとめて拾える
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname &&
      !isDeckBuilderPath(nextLocation.pathname)
  );
  const leaveOpen = blocker.state === "blocked";

  // 変更が無くなった（保存できた）のにブロックが残っていたら解除する
  useEffect(() => {
    if (blocker.state === "blocked" && !isDirty) blocker.reset?.();
  }, [blocker, isDirty]);

  // リロード・タブを閉じる・外部への遷移はブラウザ標準の確認に任せる
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // 離脱ダイアログの「保存して移動」。保存に失敗したらその場に留める
  async function saveAndLeave() {
    setLeaveSaving(true);
    const ok = await saveDeck({ skipUrlSync: true });
    setLeaveSaving(false);
    if (ok) blocker.proceed?.();
  }

  const isComplete = mainCount === MAX_DECK_SIZE;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background">
      <AppHeader />
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="flex max-w-7xl mx-auto min-h-0 flex-1 gap-3 overflow-hidden px-2 py-2 sm:px-3 sm:py-3">
          {/* デッキ内容（左・主役） */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* デッキ編集ツールバー */}
            <div className="flex flex-wrap items-center gap-2 px-1 pb-3 pt-1">
              <Input
                value={deckName}
                onChange={(e) => { setDeckName(e.target.value); setSaveStatus("idle"); }}
                placeholder="デッキ名"
                className="w-full font-semibold sm:w-48"
              />
              <Select
                value={format}
                onValueChange={(v) => { setFormat(v as DeckFormat); setSaveStatus("idle"); }}
              >
                <SelectTrigger className="min-w-0 flex-1 sm:w-auto sm:flex-none sm:shrink-0" title="フォーマット（使用可能弾の範囲）">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DECK_FORMATS.map((f) => (
                    <SelectItem key={f} value={f}>{FORMAT_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {importedFrom && !activeDeckId && (
                <span className="text-xs text-muted-foreground">
                  {importedFrom} をインポートしました（保存するとあなたのデッキになります）
                </span>
              )}
              {/* デッキメモ（回し方・採用理由など）。保存ボタンでデッキと一緒に保存する */}
              <Button
                variant="outline"
                onClick={() => setMemoOpen(true)}
                className="gap-1.5 sm:ml-auto"
                title={memo.trim() ? memo : "デッキメモを書く"}
              >
                <NotebookPen />
                メモ
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="その他の操作">
                    <Ellipsis />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => toggleImportDialog(true)}>
                    <Download />
                    インポート
                  </DropdownMenuItem>
                  {/* デッキコードは保存済みデッキにのみ存在する */}
                  <DropdownMenuItem disabled={!shareCode || !activeDeckId} onSelect={copyShareCode}>
                    <Copy />
                    デッキコードをコピー
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={deckIds.length === 0 || imageExporting} onSelect={exportDeckImage}>
                    <ImageDown />
                    画像として保存
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                // 保存中も見た目は変えない（disabled にすると半透明になり点滅して見える）。二重保存だけ防ぐ
                onClick={() => { if (saveStatus !== "saving") saveDeck(); }}
                disabled={!isComplete}
                title={isComplete ? "デッキを保存" : `保存にはメイン${MAX_DECK_SIZE}枚必要です`}
              >
                保存
              </Button>
            </div>

            {/* デッキ内容（card形式・右のカード検索と統一） */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border bg-panel elev-md">
              {/* ヘッダー: 枚数・キーカード */}
              <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-2.5">
              <span className="shrink-0 text-sm font-semibold">デッキ内容</span>
              <span
                className="shrink-0 text-xs font-medium"
                style={isComplete ? { color: "var(--civ-nature)" } : undefined}
              >
                <span className={isComplete ? "" : "text-muted-foreground"}>
                  {mainCount}/{MAX_DECK_SIZE}
                </span>
              </span>
              {hyperCount > 0 && (
                <span
                  className="shrink-0 text-xs font-medium"
                  style={{ color: hyperCount > HYPERSPATIAL_SOFT_CAP ? "var(--civ-fire)" : "var(--civ-darkness)" }}
                >
                  超次元 {hyperCount}{hyperCount > HYPERSPATIAL_SOFT_CAP ? `（目安${HYPERSPATIAL_SOFT_CAP}枚）` : ""}
                </span>
              )}
              {/* キーカード（デッキの主軸。デッキ内のカードから1枚だけ選ぶ） */}
              <button
                onClick={() => setKeyModalOpen(true)}
                disabled={deckCardsSorted.length === 0}
                className="flex h-[34px] max-w-xs items-center gap-2 rounded-md bg-secondary px-2 text-left transition-colors hover:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span className="flex shrink-0 items-center gap-1 rounded bg-primary/15 px-1 text-[10px] font-bold leading-tight text-primary">
                  <Star className="size-2.5" />
                  KEY
                </span>
                {keyCard ? (
                  <span className="min-w-0 truncate text-xs text-foreground">{keyCard.name}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">キーカード未設定</span>
                )}
              </button>
            </div>

            <div className="no-scrollbar flex-1 overflow-y-auto p-2 max-lg:pb-20">
              <div className="space-y-3">
                <div className={CARD_GRID_DECK}>
                  {deckMainCards.map((card, i) => (
                    <button key={`${card.id}-${i}`} onClick={() => setQtyCardId(card.id)}
                      title={`${card.name} の枚数を調整`}
                      className="relative cursor-pointer rounded-md transition-transform hover:z-10 hover:scale-105">
                      <CardThumb card={card} imageUrl={getCardImage(chosenArt.get(card.id) ?? card.id)} />
                    </button>
                  ))}
                </div>
                {deckHyperCards.length > 0 && (
                  <div>
                    <div
                      className="mb-1.5 border-t border-border pt-2 text-xs font-semibold"
                      style={{ color: "var(--civ-darkness)" }}
                    >
                      超次元ゾーン（{deckHyperCards.length}）
                    </div>
                    <div className={CARD_GRID_DECK}>
                      {deckHyperCards.map((card, i) => (
                        <button key={`${card.id}-${i}`} onClick={() => setQtyCardId(card.id)}
                          title={`${card.name} の枚数を調整`}
                          className="relative cursor-pointer rounded-md transition-transform hover:z-10 hover:scale-105">
                          <CardThumb card={card} imageUrl={getCardImage(chosenArt.get(card.id) ?? card.id)} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* デッキ分析（文明バランス・マナカーブ）。デッキ内容と一緒にスクロールする */}
                <div className="border-t border-border px-1 pt-3">
                  <DeckAnalysis cards={deckMainCards} />
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* カードプール（右・カード検索） */}
          {/* スマホ: 下から出るボトムシート / PC: 右の固定パネル */}
          {searchOpen && (
            <div
              className="fixed inset-0 z-[1090] bg-black/50 lg:hidden"
              onClick={() => setSearchOpen(false)}
              aria-hidden
            />
          )}
          <aside
            aria-label="カード検索"
            className={`flex flex-col overflow-hidden border border-border bg-panel elev-md
              max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-[1100] max-lg:h-dvh max-lg:border-0 max-lg:transition-[transform,visibility] max-lg:duration-300
              ${searchOpen ? "" : "max-lg:invisible max-lg:translate-y-full"}
              lg:w-96 lg:shrink-0 lg:rounded-md`}
          >
            <div className="shrink-0 space-y-2 border-b border-border px-3 pb-3 pt-3 max-lg:max-h-[55%] max-lg:overflow-y-auto">
              <div className="flex items-center justify-between gap-1.5 text-base font-semibold">
                カード検索
                <Button
                  variant="ghost"
                  size="icon"
                  className="-my-1 lg:hidden"
                  onClick={() => setSearchOpen(false)}
                  aria-label="カード検索を閉じる"
                >
                  <X />
                </Button>
              </div>
              {/* 文明（複数選択・無色含む） */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">文明</Label>
                <div className="flex flex-wrap gap-1.5">
                  {CIV_TOKENS.map((t) => (
                    <FilterChip
                      key={t}
                      active={selectedCivs.includes(t)}
                      activeStyle={civActiveStyle(t)}
                      onClick={() => toggleCiv(t)}
                    >
                      {CIV_TOKEN_LABELS[t]}
                    </FilterChip>
                  ))}
                </div>
              </div>
              {/* 多色/単色の一致モード（択一・選択文明と連動） */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">多色・単色</Label>
                <div className="flex flex-wrap gap-1.5">
                  {CIV_MODES.map((m) => (
                    <FilterChip
                      key={m.value}
                      active={civMode === m.value}
                      activeStyle={MODE_ACTIVE_STYLE}
                      onClick={() => toggleCivMode(m.value)}
                    >
                      {m.label}
                    </FilterChip>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">タイプ</Label>
                  <Select
                    value={typeFilter || ALL}
                    onValueChange={(v) => setTypeFilter(v === ALL ? "" : (v as CardType))}
                  >
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>全タイプ</SelectItem>
                      {(Object.entries(TYPE_LABELS) as [CardType, string][]).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">コスト</Label>
                  <Select
                    value={costFilter === "" ? ALL : String(costFilter)}
                    onValueChange={(v) => setCostFilter(v === ALL ? "" : Number(v))}
                  >
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>コスト指定なし</SelectItem>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                        <SelectItem key={n} value={String(n)}>コスト {n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs text-muted-foreground">種族</Label>
                  <Select
                    value={raceFilter || ALL}
                    onValueChange={(v) => setRaceFilter(v === ALL ? "" : v)}
                  >
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>全種族</SelectItem>
                      {getAllRaces().map((race) => (
                        <SelectItem key={race} value={race}>{race}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* 能力（ブロッカー・スレイヤー等。複数選択でAND）。数が多いので折りたたむ */}
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => setAbilityOpen((v) => !v)}
                  className="flex w-full items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronDown
                    className={`size-3.5 shrink-0 transition-transform ${abilityOpen ? "" : "-rotate-90"}`}
                  />
                  <span>能力</span>
                  {selectedKeywords.length > 0 && (
                    <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary">
                      {selectedKeywords.length}
                    </span>
                  )}
                  {selectedKeywords.length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setSelectedKeywords([]); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setSelectedKeywords([]); } }}
                      className="ml-auto text-[10px] underline decoration-dotted hover:text-foreground"
                    >
                      クリア
                    </span>
                  )}
                </button>
                {abilityOpen && (
                  <div className="max-h-52 space-y-2 overflow-y-auto rounded-md border border-border bg-background/40 p-2">
                    {ABILITY_KEYWORD_GROUPS.map((group) => (
                      <div key={group.label} className="space-y-1">
                        <div className="text-[10px] font-medium text-muted-foreground">{group.label}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {group.keywords.map((kw) => (
                            <FilterChip
                              key={kw.match}
                              active={selectedKeywords.includes(kw.match)}
                              activeStyle={MODE_ACTIVE_STYLE}
                              onClick={() => toggleKeyword(kw.match)}
                            >
                              {kw.label}
                            </FilterChip>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="card-search-query" className="text-xs text-muted-foreground">キーワード</Label>
                <Input
                  id="card-search-query"
                  type="text"
                  placeholder="カード名・テキスト検索"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div className="no-scrollbar flex-1 overflow-y-auto p-2">
              {filteredCards.length === 0 ? (
                <Typography tone="muted" className="py-8 text-center">該当するカードがありません</Typography>
              ) : (
                <div className={CARD_GRID_FIXED}>
                  {visibleCards.map((card) => (
                    <button key={card.id} onClick={() => setQtyCardId(card.id)}
                      title={`${card.name}（コスト${card.cost}）の枚数を調整`}
                      className="relative cursor-pointer rounded-md transition-transform hover:z-10 hover:scale-105">
                      <CardThumb card={card} />
                    </button>
                  ))}
                </div>
              )}
              {/* 末尾に来たら次の分を描画するための目印 */}
              {hasMore && <div ref={moreRef} className="h-8" aria-hidden />}
            </div>
          </aside>
        </div>
      </div>

      {/* スマホ用: カード検索を開く丸ボタン（右下・アイコンのみ）。シートが開く間は隠す */}
      <Button
        size="icon"
        onClick={() => setSearchOpen(true)}
        aria-label="カードを検索して追加"
        className={`fixed bottom-5 right-4 z-[var(--z-raised)] size-14 rounded-full shadow-lg transition-[transform,opacity] duration-200 lg:hidden [&_svg]:size-14 [&_svg]:stroke-[2.5] ${
          searchOpen ? "pointer-events-none scale-50 opacity-0" : ""
        }`}
      >
        <Plus />
      </Button>

      {/* キーカード選択モーダル */}
      <Dialog open={keyModalOpen} onOpenChange={setKeyModalOpen}>
        <DialogContent className="grid-rows-[auto_1fr_auto] max-h-[80vh] overflow-hidden sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>キーカードを選択</DialogTitle>
            <DialogDescription>デッキの主軸となるカードを1枚</DialogDescription>
          </DialogHeader>

          <div className="-mx-1 min-h-0 overflow-y-auto px-1">
            <div className="flex flex-wrap content-start gap-3">
              {deckCardsSorted.map((card) => {
                const isKey = keyCardId === card.id;
                return (
                  <button key={card.id} onClick={() => chooseKeyCard(card.id)}
                    title={`${card.name} をキーカードにする`}
                    className={`relative rounded-md transition-transform hover:scale-105 ${
                      isKey ? "ring-2 ring-primary ring-offset-2 ring-offset-popover" : ""
                    }`}>
                    <CardThumb card={card} width={96} imageUrl={getCardImage(chosenArt.get(card.id) ?? card.id)} />
                    {isKey && (
                      <span className="absolute right-1 top-1 flex items-center gap-0.5 rounded bg-primary px-1 text-[10px] font-bold leading-tight text-primary-foreground">
                        <Star className="size-2.5" />
                        KEY
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter className="items-center sm:justify-between">
            <span className="text-xs text-muted-foreground">
              {keyCard ? `現在: ${keyCard.name}` : "現在: 未設定"}
            </span>
            <Button variant="secondary" size="sm" onClick={() => chooseKeyCard(null)} disabled={!keyCardId}>
              キーカードを解除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* デッキメモの編集モーダル */}
      <Dialog open={memoOpen} onOpenChange={setMemoOpen}>
        <DialogContent aria-describedby={undefined} className="grid-rows-[auto_1fr_auto] h-[80vh] sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>デッキメモ</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-col gap-1">
            <textarea
              value={memo}
              onChange={(e) => { setMemo(e.target.value); setSaveStatus("idle"); }}
              maxLength={MEMO_MAX_LENGTH}
              autoFocus
              placeholder="例: 序盤はマナを伸ばし、5ターン目にキーカードを着地させる"
              className="min-h-0 w-full flex-1 resize-none rounded-md border border-input bg-field px-3 py-2.5 leading-relaxed text-base md:text-sm text-foreground/80 outline-none transition-[color,box-shadow,border-color] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
            />
            <Typography variant="caption" tone="muted" className="block text-right">
              {memo.length}/{MEMO_MAX_LENGTH}
            </Typography>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMemoOpen(false)}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* デッキコードのインポートモーダル */}
      <Dialog open={importOpen} onOpenChange={toggleImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>デッキをインポート</DialogTitle>
            <DialogDescription>
              {importMode === "code"
                ? "受け取ったデッキコードを入力すると、その内容がこの画面に読み込まれます。"
                : "画像からカードを読み取って、この画面に読み込みます。"}
              保存するとあなたのデッキになります。
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" role="tablist">
            {(["code", "image"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={importMode === mode}
                onClick={() => setImportMode(mode)}
                className={`rounded-md py-1.5 text-sm transition-colors ${
                  importMode === mode ? "bg-background font-medium shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {mode === "code" ? "デッキコード" : "画像から読み取り"}
              </button>
            ))}
          </div>
          {importMode === "image" ? (
            <DeckImageImport
              maxCopies={MAX_COPIES}
              onImport={importFromImage}
              onCancel={() => toggleImportDialog(false)}
            />
          ) : (<>
          <div className="space-y-2">
            <Input
              value={importCode}
              onChange={(e) => { setImportCode(e.target.value); setImportError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") importByCode(); }}
              placeholder="例: ABCD-EFGH-JKMN"
              autoFocus
              spellCheck={false}
              className="font-mono tracking-wider"
            />
            {importError && <Typography variant="caption" tone="destructive">{importError}</Typography>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => toggleImportDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={importByCode} disabled={importing || !importCode.trim()}>
              {importing ? "読み込み中..." : "インポート"}
            </Button>
          </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* 未保存の変更がある状態で他ページへ移動しようとしたときの確認モーダル */}
      <Dialog open={leaveOpen} onOpenChange={(o) => { if (!o) blocker.reset?.(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>変更が保存されていません</DialogTitle>
            <DialogDescription>
              このまま移動すると、編集中の内容は失われます。
              {!isComplete && `（保存にはメイン${MAX_DECK_SIZE}枚必要です。現在${mainCount}枚）`}
            </DialogDescription>
          </DialogHeader>
          {saveStatus === "error" && saveError && (
            <Typography variant="caption" tone="destructive">{saveError}</Typography>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="secondary" onClick={() => blocker.reset?.()} disabled={leaveSaving}>
              編集を続ける
            </Button>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={() => blocker.proceed?.()} disabled={leaveSaving}>
                破棄して移動
              </Button>
              {isComplete && (
                <Button onClick={saveAndLeave} disabled={leaveSaving}>
                  {leaveSaving ? "保存中..." : "保存して移動"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 枚数調整モーダル */}
      <Dialog open={!!qtyCardId} onOpenChange={(o) => { if (!o) setQtyCardId(null); }}>
        <DialogContent className="sm:max-w-md">
          {qtyCard && (
            <>
              <DialogHeader>
                <DialogTitle className="truncate">{qtyCard.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4">
                <div className="w-[min(300px,100%)]">
                  <CardThumb card={qtyCard} imageUrl={getCardImage(qtyArtId!)} full />
                </div>
                {qtyCard.arts && qtyCard.arts.length > 1 && (
                  <div className="w-full">
                    <div className="mb-1.5 text-xs text-muted-foreground">イラストを選ぶ（{qtyCard.arts.length}種）</div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {qtyCard.arts.map((art) => {
                        const selected = art.id === qtyArtId;
                        return (
                          <button
                            key={art.id}
                            type="button"
                            onClick={() => chooseArt(qtyCard.id, art.id)}
                            title={art.set}
                            aria-pressed={selected}
                            className={`rounded-md ring-2 transition ${selected ? "ring-primary" : "ring-transparent hover:ring-muted-foreground/40"}`}
                          >
                            <CardThumb card={qtyCard} width={56} imageUrl={art.imageUrl} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => removeCard(qtyCard.id)}
                    disabled={qtyCount === 0}
                    title="1枚減らす"
                  >
                    <Minus />
                  </Button>
                  <span className="w-14 text-center font-mono text-2xl font-bold">×{qtyCount}</span>
                  <Button
                    size="icon"
                    onClick={() => addCard(qtyCard)}
                    disabled={qtyCount >= MAX_COPIES}
                    title="1枚増やす"
                  >
                    <Plus />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
